/**
 * ApprovalFidelityDetails — the anti "lies-in-the-loop" body of an approval card.
 *
 * Approval dialogs get rubber-stamped when they show only the agent's own
 * framing. This renders, as the PRIMARY content, the RAW operation the agent is
 * about to perform (tool name + full pretty-printed payload), the decision-log
 * line (WHY approval was required — which tier/grant/rule), and the risk context
 * (tier · module · background vs interactive), plus expiry countdown + escalation
 * state. The agent's natural-language summary stays, but the caller renders it
 * as secondary chrome around this block.
 *
 * Shared by the Supervision-tab / inbox `PendingApprovalCard` and the inline
 * chat `ToolCallBlock`, so both surfaces show identical fidelity.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock, Info, ShieldAlert, Terminal } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import { LtrToken } from '../common';
import type { Approval, ApprovalDecisionContext } from '../../types/approval';

/** Keys the backend stores inside action_payload for its own bookkeeping — not
 * part of the operation the human is reviewing, so they're surfaced separately
 * (tool name) or drive UI state (truncation), never dumped into the raw JSON. */
const META_PAYLOAD_KEYS = new Set(['_tool_name', '_truncated', 'summary']);

/** Approvals expiring within this window are visually urgent. */
const EXPIRING_SOON_MS = 2 * 60 * 60 * 1000; // 2 hours

/** Live "expires in 2h 15m" / "Expired" countdown, ticking each minute. */
function ExpiryCountdown({ expiresAt }: { expiresAt: string }) {
  const { t } = useTranslation('aiAgents');
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const remaining = new Date(expiresAt).getTime() - now;
  const urgent = remaining > 0 && remaining <= EXPIRING_SOON_MS;

  let label: string;
  if (remaining <= 0) {
    label = t('octopus.approvals.fidelity.expired');
  } else {
    const totalMinutes = Math.floor(remaining / 60_000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    const parts = days > 0
      ? t('octopus.approvals.fidelity.remainingDays', { days, hours })
      : hours > 0
        ? t('octopus.approvals.fidelity.remainingHours', { hours, minutes })
        : t('octopus.approvals.fidelity.remainingMinutes', { minutes });
    label = t('octopus.approvals.fidelity.expiresIn', { time: parts });
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1',
        remaining <= 0 ? 'text-destructive' : urgent ? 'text-warning font-medium' : 'text-muted-foreground',
      )}
    >
      <Clock className="h-3 w-3 shrink-0" />
      {label}
    </span>
  );
}

interface ApprovalFidelityDetailsProps {
  approval: Approval;
  /** Compact spacing for the inline chat surface. */
  compact?: boolean;
  className?: string;
}

export function ApprovalFidelityDetails({ approval, compact = false, className }: ApprovalFidelityDetailsProps) {
  const { t } = useTranslation('aiAgents');
  const payload = approval.action_payload ?? {};
  const ctx: ApprovalDecisionContext = approval.decision_context ?? {};

  const toolName = String(payload._tool_name ?? approval.action_type);
  const truncated = payload._truncated === true;

  // The raw operation payload the human is actually authorizing — meta keys
  // removed. When truncated the executable body wasn't stored; show the stored
  // preview string instead and flag it clearly as non-executable.
  const rawEntries = Object.entries(payload).filter(([k]) => !META_PAYLOAD_KEYS.has(k));
  const rawBody = truncated
    ? String(payload.summary ?? '')
    : JSON.stringify(Object.fromEntries(rawEntries), null, 2);

  const isEscalated = approval.status === 'escalated' || !!approval.escalated_at;

  const riskBadges: Array<{ key: string; label: string }> = [];
  if (ctx.tool_tier) riskBadges.push({ key: 'tier', label: t('octopus.approvals.fidelity.tier', { tier: ctx.tool_tier }) });
  if (ctx.module) riskBadges.push({ key: 'module', label: ctx.module });
  if (ctx.trigger) {
    riskBadges.push({
      key: 'trigger',
      label: t(`octopus.approvals.fidelity.trigger.${ctx.trigger}`, {
        defaultValue: ctx.trigger,
      }),
    });
  }
  if (ctx.is_background != null) {
    riskBadges.push({
      key: 'context',
      label: ctx.is_background
        ? t('octopus.approvals.fidelity.background')
        : t('octopus.approvals.fidelity.interactive'),
    });
  }

  return (
    <div className={cn(compact ? 'space-y-2' : 'space-y-3', className)}>
      {/* Raw operation — the primary, un-spun view of what will run. */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <Terminal className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
            {t('octopus.approvals.fidelity.rawOperation')}
          </span>
          {truncated && (
            <Badge className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">
              <AlertTriangle className="h-3 w-3 mr-0.5" />
              {t('octopus.approvals.fidelity.truncated')}
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground mb-1">
          {t('octopus.approvals.fidelity.tool')}{' '}
          <LtrToken className="font-mono text-foreground break-all">{toolName}</LtrToken>
        </div>
        <pre className="text-xs bg-muted/60 rounded-md p-2 overflow-x-auto max-h-64 whitespace-pre-wrap break-words text-foreground">
          {rawBody || t('octopus.approvals.fidelity.noPayload')}
        </pre>
        {truncated && (
          <p className="text-[11px] text-muted-foreground mt-1">
            {t('octopus.approvals.fidelity.truncatedHint')}
          </p>
        )}
      </div>

      {/* Decision log — WHY approval was required. */}
      {ctx.reason && (
        <div className="flex items-start gap-1.5 text-xs">
          <Info className="h-3.5 w-3.5 text-info shrink-0 mt-0.5" />
          <span className="text-muted-foreground">
            <span className="font-medium text-foreground">
              {t('octopus.approvals.fidelity.whyApproval')}{' '}
            </span>
            {ctx.reason}
          </span>
        </div>
      )}

      {/* Risk context + lifecycle. */}
      {(riskBadges.length > 0 || approval.expires_at || isEscalated) && (
        <div className="flex items-center gap-1.5 flex-wrap text-xs">
          {riskBadges.length > 0 && <ShieldAlert className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
          {riskBadges.map((b) => (
            <Badge key={b.key} variant="secondary" className="text-[10px] font-normal">
              {b.label}
            </Badge>
          ))}
          {isEscalated && (
            <Badge className="text-[10px] bg-warning/10 text-warning border-warning/20">
              {approval.escalated_to_name
                ? t('octopus.approvals.fidelity.escalatedTo', { name: approval.escalated_to_name })
                : t('octopus.approvals.fidelity.escalated')}
            </Badge>
          )}
          {approval.expires_at && <ExpiryCountdown expiresAt={approval.expires_at} />}
        </div>
      )}
    </div>
  );
}
